import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../hooks/useTheme';
import { useTilt } from '../hooks/useTilt';
import { StarIcon, QuoteIcon, BadgeCheckIcon } from 'lucide-react';
interface TestimonialCardProps {
  quote: string;
  name: string;
  title: string;
  company: string;
  avatarUrl: string;
  index: number;
}
function TestimonialCard({
  quote,
  name,
  title,
  company,
  avatarUrl,
  index
}: TestimonialCardProps) {
  const { isDark } = useTheme();
  const { tilt, ref, handleMouseMove, handleMouseLeave } = useTilt(10);
  return (
    <motion.div
      id="testimonials"
      initial={{
        opacity: 0,
        y: 40
      }}
      whileInView={{
        opacity: 1,
        y: 0
      }}
      viewport={{
        once: true,
        margin: '-50px'
      }}
      transition={{
        type: 'spring',
        stiffness: 100,
        damping: 20,
        delay: index * 0.1
      }}
      className="perspective-container">

      <motion.div
        ref={ref}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        animate={{
          rotateX: tilt.rotateX,
          rotateY: tilt.rotateY,
          scale: tilt.scale
        }}
        transition={{
          type: 'spring',
          stiffness: 150,
          damping: 15
        }}
        className={`relative rounded-2xl p-8 h-full flex flex-col transition-all duration-200 cursor-default overflow-hidden group ${isDark ? 'bg-white/[0.03] border border-white/[0.06] hover:border-cyan-400/40 hover:shadow-xl hover:shadow-cyan-500/15' : 'bg-white/70 border border-slate-200/60 hover:border-cyan-300/60 hover:shadow-xl hover:shadow-cyan-500/10'}`}
        style={{
          transformStyle: 'preserve-3d',
          willChange: 'transform',
          backdropFilter: 'blur(4px)'
        }}>

        {/* Animated gradient border overlay on hover */}
        <div
          className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none`}>

          <div className="absolute inset-0 rounded-2xl animated-border p-[1px]">
            <div
              className={`w-full h-full rounded-2xl ${isDark ? 'bg-slate-950/95' : 'bg-white/95'}`} />

          </div>
        </div>

        {/* Subtle dot pattern background */}
        <div
          className={`absolute inset-0 opacity-[0.03] pointer-events-none`}
          style={{
            backgroundImage: `radial-gradient(circle, ${isDark ? '#06b6d4' : '#0891b2'} 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }} />


        {/* Content */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Quote icon with float */}
          <motion.div
            animate={{
              y: [0, -4, 0]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
            className={`mb-4 ${isDark ? 'text-cyan-500/25' : 'text-cyan-200/60'}`}>

            <QuoteIcon className="w-10 h-10" />
          </motion.div>

          {/* Stars with sequential animation */}
          <div className="flex gap-1 mb-5">
            {[0, 1, 2, 3, 4].map((i) =>
              <motion.div
                key={i}
                initial={{
                  opacity: 0,
                  scale: 0
                }}
                whileInView={{
                  opacity: 1,
                  scale: 1
                }}
                viewport={{
                  once: true
                }}
                transition={{
                  delay: index * 0.1 + 0.3 + i * 0.05,
                  type: 'tween',
                  duration: 0.3
                }}>

                <StarIcon className="w-4 h-4 fill-amber-400 text-amber-400" />
              </motion.div>
            )}
          </div>

          {/* Quote */}
          <blockquote
            className={`text-[15px] leading-relaxed mb-8 flex-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>

            "{quote}"
          </blockquote>

          {/* Author */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <img
                src={avatarUrl}
                alt={name}
                className={`w-14 h-14 rounded-full object-cover ring-2 transition-all duration-200 group-hover:ring-cyan-400/50 ${isDark ? 'ring-white/10' : 'ring-slate-200'}`} />

              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center ring-2 ring-slate-950">
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}>

                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7" />

                </svg>
              </div>
            </div>
            <div>
              <div
                className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>

                {name}
              </div>
              <div
                className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>

                {title}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <BadgeCheckIcon
                  className={`w-3.5 h-3.5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />

                <span
                  className={`text-xs font-medium ${isDark ? 'text-cyan-400/80' : 'text-cyan-600/80'}`}>

                  {company}
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>);

}
export function Testimonials() {
  const { isDark } = useTheme();
  return (
    <section className="relative py-28 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{
            opacity: 0,
            y: 20
          }}
          whileInView={{
            opacity: 1,
            y: 0
          }}
          viewport={{
            once: true
          }}
          transition={{
            type: 'spring',
            stiffness: 100,
            damping: 20
          }}
          className="text-center mb-16">

          <span
            className={`inline-block text-sm font-semibold tracking-wider uppercase mb-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>

            Testimonials
          </span>
          <h2
            className={`text-3xl md:text-5xl font-bold tracking-[-0.02em] mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>

            Loved by teams <span className="gradient-text">worldwide</span>
          </h2>
          <p
            className={`text-lg max-w-xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>

            See why thousands of businesses trust 1401 for their payment
            infrastructure.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <TestimonialCard
            quote="1401 transformed our checkout experience. We saw a 34% increase in conversion rates within the first month. The API documentation is the best I've ever worked with."
            name="Sarah Chen"
            title="CTO"
            company="Flowbase"
            avatarUrl="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face"
            index={0} />

          <TestimonialCard
            quote="Moving from our legacy processor to 1401 was seamless. Their migration team handled everything, and we were live in under a week. Settlement times went from 3 days to instant."
            name="Marcus Rivera"
            title="VP Engineering"
            company="Stackshift"
            avatarUrl="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
            index={1} />

          <TestimonialCard
            quote="The fraud detection alone saved us over $2M last year. 1401 AI catches things our previous system never would. It's not just a payment processor — it's a competitive advantage."
            name="Elena Kowalski"
            title="Head of Payments"
            company="Meridian Commerce"
            avatarUrl="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
            index={2} />

          <TestimonialCard
            quote="The API quality is outstanding. We integrated 1401 into our platform in just 2 days. The developer experience is leagues ahead of any other payment provider we've used."
            name="David Park"
            title="CEO"
            company="TechFlow"
            avatarUrl="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
            index={3} />

          <TestimonialCard
            quote="We cut our payment processing costs by 40% after switching to 1401. The transparent pricing and zero hidden fees made budgeting predictable for the first time."
            name="Amira Hassan"
            title="CFO"
            company="GlobalRetail"
            avatarUrl="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face"
            index={4} />

          <TestimonialCard
            quote="99.99% uptime isn't just marketing — we've tracked it for 18 months straight. 1401 infrastructure reliability is unmatched. Zero payment outages since we migrated."
            name="James Wright"
            title="CTO"
            company="CloudScale"
            avatarUrl="https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=100&h=100&fit=crop&crop=face"
            index={5} />

        </div>
      </div>
    </section>);

}